import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import type { Query } from '@/api/query'
import { useQuery } from '@/api/query'
import { formatDateIn } from '@/lib/format'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { IST, STAGE_LABELS, STAGE_TONES, formatScore } from './stages'

type Lead = Schema<'LeadOut'>
type LeadsQuery = Query<Schema<'LeadListResponse'>>

/** `DEFAULT_PAGE` in `app/v1/leads.py`, mirrored — OpenAPI describes a
 *  parameter's bounds and not its default. */
const PAGE = 25

/** Indian digit grouping — *1,284*, not *1284* and not *1,284* the American way
 *  once the numbers get past a lakh. The product is India-facing (CLAUDE.md), and
 *  a lead count is one of the few figures on these screens big enough to need
 *  grouping at all. */
const GROUPED = new Intl.NumberFormat('en-IN')

/** The stage filter, in §5's pipeline order. `lost` is last because it is the
 *  one a tenant reaches for deliberately rather than while scanning. */
const STAGES: readonly { value: string; label: string }[] = [
  { value: 'found', label: 'Found' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'replied', label: 'Replied' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
]

/** Which channels a lead can actually be reached on. Mirrors
 *  `CHANNEL_CONTACT_KINDS` in `app/db/outreach.py`; `ivr` is absent there
 *  because it is a channel that no longer exists (§5), so it is absent here. */
const CHANNELS: readonly { value: string; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'rcs', label: 'RCS' },
  { value: 'linkedin', label: 'LinkedIn' },
]

/**
 * The leads list — `design/screens/outreach.html#leads`, audit row §B1.
 *
 * S-B13 ported `../linkmailer`'s discovery libraries and recorded leads from the
 * first scrape onward; nothing has ever shown them. This is that screen, and
 * `#lead` beside it is where one of these rows opens.
 *
 * WHY THE HEADERS DO NOT SORT, WHICH THE PROTOTYPE DRAWS
 * -------------------------------------------------------
 * The prototype's table sorts on Name, Company, Stage and Score by clicking a
 * header, which is a client-side sort of a page the client happens to hold. This
 * list is keyset-paginated (standards §4 forbids offset), so a sort is a
 * *server* ordering with a cursor shaped for it — and the API offers two:
 * newest-first and best-score-first. Those are the two orderings with a use.
 * Sorting by name is *find this person*, which the search box does better and
 * across every page rather than across the twenty-five in hand.
 *
 * So the ordering is a control that says what it does, and the two columns that
 * cannot order are plain headers rather than buttons that sort one page and lie
 * about the rest.
 *
 * WHY THE COUNT IS "IN TOTAL" AND NEVER THE NUMBER OF MATCHES
 * -------------------------------------------------------------
 * `LeadListResponse.total` is `OutreachTransaction.lead_count`, which counts the
 * whole table and takes no filter — the same shape the suppression list has, and
 * the same trap: a filtered list beside an unfiltered count reads as though the
 * count described the filter. The toolbar labels it, and says how many are on
 * screen separately.
 */
export function Leads(): JSX.Element {
  const [search, setSearch] = useState('')
  const [stage, setStage] = useState('')
  const [channel, setChannel] = useState('')
  const [order, setOrder] = useState<'recent' | 'score'>('recent')
  // The cursor stack, so *Previous* is a pop rather than a second cursor
  // direction the API would have to grow. `null` at the head is the first page.
  const [cursors, setCursors] = useState<(string | null)[]>([null])

  const cursor = cursors[cursors.length - 1] ?? null

  // Not debounced, for the reason `InboxPage` states: `useQuery` aborts the
  // previous request on every change, so a fast typist produces one in-flight
  // request rather than a queue of them.
  const list = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/leads', {
          params: {
            query: {
              limit: PAGE,
              order,
              cursor,
              stage: stage || null,
              channel: channel || null,
              search: search || null,
            },
          },
          signal,
        }),
      ),
    [order, cursor, stage, channel, search],
  )

  const reset = (): void => {
    setCursors([null])
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">Leads</h1>
        <span className="app-spacer" />
        <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/outreach/compose">
          What we say
        </Link>
      </div>

      <div className="ar-card">
        <div className="ar-table-toolbar">
          <input
            className="ar-input"
            type="search"
            placeholder="Search name, company, title…"
            aria-label="Search leads"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              reset()
            }}
          />
          <select
            className="ar-select app-leads__filter"
            aria-label="Filter by stage"
            value={stage}
            onChange={(event) => {
              setStage(event.target.value)
              reset()
            }}
          >
            <option value="">All stages</option>
            {STAGES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="ar-select app-leads__filter"
            aria-label="Filter by channel"
            value={channel}
            onChange={(event) => {
              setChannel(event.target.value)
              reset()
            }}
          >
            <option value="">All channels</option>
            {CHANNELS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            className="ar-select app-leads__filter"
            aria-label="Order"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value === 'score' ? 'score' : 'recent')
              reset()
            }}
          >
            <option value="recent">Newest first</option>
            <option value="score">Best score first</option>
          </select>
          <span className="ar-card__spacer" />
          <span className="ar-subtle app-leads__count">
            {list.state.status === 'ready'
              ? `${String(list.state.data.leads.length)} shown · ${GROUPED.format(list.state.data.total)} in total`
              : null}
          </span>
        </div>

        <Body list={list} />

        <div className="ar-pagination">
          <span className="ar-pagination__info" aria-live="polite">
            {list.state.status === 'ready' ? `Page ${String(cursors.length)}` : ''}
          </span>
          <Button
            size="sm"
            disabled={cursors.length === 1}
            onClick={() => {
              setCursors((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack))
            }}
          >
            Previous
          </Button>
          <Button
            size="sm"
            disabled={list.state.status !== 'ready' || list.state.data.next_cursor === null}
            onClick={() => {
              if (list.state.status !== 'ready' || list.state.data.next_cursor === null) return
              const next: string | null = list.state.data.next_cursor ?? null
              setCursors((stack) => [...stack, next])
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}

function Body({ list }: { list: LeadsQuery }): JSX.Element {
  if (list.state.status === 'loading') return <Loading label="Loading leads" lines={6} />
  if (list.state.status === 'error')
    return (
      <div className="ar-card__body">
        <ErrorState
          failure={list.state.failure}
          onRetry={list.refetch}
          title="Could not load your leads"
        />
      </div>
    )
  if (list.state.data.leads.length === 0)
    return (
      <div className="ar-card__body">
        <Empty
          icon="⌕"
          title="No leads here yet"
          body="Leads arrive from the post searches your browser extension runs. Once one lands, this is where it appears — with the post it came from and why it scored the way it did."
        />
      </div>
    )

  return (
    <div className="ar-table-wrap">
      <table className="ar-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Company</th>
            <th>Title</th>
            <th>Channel</th>
            <th>Stage</th>
            <th className="ar-num">Score</th>
            <th>Found</th>
          </tr>
        </thead>
        <tbody>
          {list.state.data.leads.map((lead) => (
            <Row key={lead.id} lead={lead} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Row({ lead }: { lead: Lead }): JSX.Element {
  return (
    <tr>
      <td>
        <Link className="ar-link" to={`/outreach/leads/${lead.id}`}>
          <strong>{lead.full_name ?? 'Unnamed lead'}</strong>
        </Link>
      </td>
      <td>{lead.company ?? '—'}</td>
      <td className="app-leads__headline">{lead.headline ?? '—'}</td>
      <td>
        <ChannelCell lead={lead} />
      </td>
      <td>
        <span className={`ar-badge ar-badge--${STAGE_TONES[lead.stage] ?? 'neutral'}`}>
          {STAGE_LABELS[lead.stage] ?? lead.stage}
        </span>
      </td>
      <td className="ar-num">{formatScore(lead.relevance_score ?? null)}</td>
      <td className="ar-muted">{formatDateIn(lead.first_seen_at, IST)}</td>
    </tr>
  )
}

/**
 * The channel column, and it says *how many* addresses rather than only which.
 *
 * The prototype shows one channel per lead. `contact_count` is on the response
 * because one is often not the whole truth — a lead who published an email and a
 * mobile is reachable on both, and the ladder's *Switch channel* rung is the
 * whole reason the second one matters. Showing only the first would make that
 * rung look like it came from nowhere.
 */
function ChannelCell({ lead }: { lead: Lead }): JSX.Element {
  const kind = lead.contact_kind ?? null
  if (kind === null)
    return (
      <span className="ar-subtle" title="No address was found in this lead's post">
        No address
      </span>
    )
  const extra = lead.contact_count - 1
  return (
    <span>
      {CONTACT_LABELS[kind] ?? kind}
      {extra > 0 ? <span className="ar-subtle"> +{extra}</span> : null}
    </span>
  )
}

/** `CONTACT_KINDS` in `app/db/models/tenant.py`, in the tenant's words. */
const CONTACT_LABELS: Record<string, string> = {
  email: 'Email',
  mobile: 'Mobile',
  linkedin: 'LinkedIn',
}
